<template>
   <div
      class="supplies-vendor-net-amount"
      :data-grid-section="gridSection"
      :data-grid-table="gridTableIndex"
      :data-grid-row="gridRowIndex"
      :data-grid-col="gridColIndex"
   >
      <div
         class="currency-input"
         :class="{focused: isFocused, disabled}"
         @mousedown.self.prevent="focusInput"
      >
         <span class="currency-symbol">$</span>

         <input
            ref="input"
            type="text"
            class="currency-field"
            :disabled="disabled"
            :value="displayValue"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            :placeholder="placeholder"
         />
      </div>
   </div>
</template>
<script>
import {formatCurrency} from '@/helpers/utils';
import {hasValueChanged, normalizeNullableCents} from '@/helpers/expenseInput';

export default {
   name: 'SuppliesVendorNetAmount',

   props: {
      gridSection: String,
      gridTableIndex: Number,
      gridRowIndex: Number,
      gridColIndex: Number,
      ident: [String, Number],
      summary: {
         type: Object,
         default: () => ({}),
      },
      assigneeId: {
         type: String,
         default: '',
      },
      placeholder: {
         type: String,
         default: null,
      },
      disabled: {
         type: Boolean,
         default: false,
      },
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         rawValue: '',
         isFocused: false,
         initialAmountCents: null,
      };
   },

   computed: {
      amountValue() {
         return this.summary?.totalDebit;
      },

      displayValue() {
         if (this.isFocused) {
            return this.rawValue;
         }

         if (this.amountValue != null) {
            return formatCurrency(this.amountValue / 100);
         }

         return '';
      },
   },

   methods: {
      focusInput() {
         if (this.disabled) {
            return;
         }

         this.$refs.input?.focus();
      },

      onFocus() {
         this.isFocused = true;
         this.initialAmountCents = this.amountValue ?? null;

         if (this.amountValue != null) {
            this.rawValue = (this.amountValue / 100).toFixed(2);
         } else {
            this.rawValue = '';
         }

         this.$nextTick(() => {
            this.$refs.input.select();
         });
      },

      normalize(value) {
         if (!value) return '';

         let cleaned = value.replace(/[^\d.]/g, '');

         const dotIndex = cleaned.indexOf('.');
         if (dotIndex !== -1) {
            const integer = cleaned.slice(0, dotIndex);
            const decimal = cleaned
               .slice(dotIndex + 1)
               .replace(/\./g, '')
               .slice(0, 2);

            cleaned = `${integer}.${decimal}`;
         }

         return /^(?:\d+|\d*\.\d{0,2})$/.test(cleaned) ? cleaned : '';
      },

      onInput(e) {
         const input = e.target.value;
         const normalized = this.normalize(input);

         this.rawValue = normalized;

         if (normalized !== input) {
            this.$refs.input.value = normalized;
         }
      },
      async saveNetAmount(value) {
         await this.$store.dispatch(`${this.expenseType}/saveNetAmount`, {
            companyId: this.$companyId,
            summary: {...this.summary, assigneeId: this.assigneeId},
            netAmount: value,
         });

         this.$nextTick(() => {
            this.rawValue = '';
         });
      },

      async onBlur() {
         this.isFocused = false;
         const nextCents = normalizeNullableCents(this.rawValue);
         if (!hasValueChanged(nextCents, this.initialAmountCents)) {
            this.rawValue = '';
            return;
         }

         try {
            await this.saveNetAmount(nextCents);
         } catch {
            // The store action handles rollback and user alerting.
         }
      },
   },
};
</script>
<style scoped>
.currency-input {
   position: relative;
   display: flex;
   align-items: center;
   height: 32px;
   min-width: 8rem;
   border: 1px solid #bbc7d4;
   border-radius: 4px;
   background: white;
   cursor: text;
}

.currency-input.focused {
   border-color: #80bdff;
   box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.currency-input.disabled {
   background: #f5f5f5;
   cursor: not-allowed;
}

.currency-symbol {
   position: absolute;
   left: 10px;
   color: grey;
   pointer-events: none;
   font-size: 1rem;
}

.currency-field {
   width: 100%;
   height: 100%;
   border: none;
   outline: none;
   padding-left: 22px;
   background: transparent;
   font-size: 0.75rem;
   font-weight: 800;
   color: #46505a;
}

.currency-field::placeholder {
   color: #bbc7d4;
}
</style>
